import { CheckCircle2, FileText, Loader2, UploadCloud, X, XCircle } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { fmtSize } from '../../meetingFiles'

export type PendingReceipt = { key: string; file: File; state: 'waiting' | 'uploading' | 'done' | 'error'; error?: string }

const ACCEPT = ['application/pdf', 'image/png', 'image/jpeg', 'image/webp']
const MAX = 10 * 1024 * 1024

/** Çoklu fiş/dekont: sürükle-bırak ya da seç, görsel önizleme, dosya başına durum (yükleniyor / tamam / hata). */
export function ReceiptDrop({ items, onChange, max, disabled }: { items: PendingReceipt[]; onChange: (v: PendingReceipt[]) => void; max: number; disabled?: boolean }) {
  const { t } = useTranslation()
  const ref = useRef<HTMLInputElement>(null)
  const [over, setOver] = useState(false)
  const [error, setError] = useState('')
  const take = (files: FileList | null) => {
    if (!files) return
    const next = [...items]
    for (const f of Array.from(files)) {
      if (next.length >= max) {
        setError(t('crm.expenses.tooManyFiles', { max }))
        break
      }
      if (!ACCEPT.includes(f.type)) {
        setError(t('crm.expenses.badType'))
        continue
      }
      if (f.size > MAX) {
        setError(t('errors.FILE_TOO_LARGE'))
        continue
      }
      next.push({ key: `${f.name}-${f.size}-${f.lastModified}-${Math.random()}`, file: f, state: 'waiting' })
    }
    onChange(next)
  }
  return (
    <div className="space-y-2">
      <button
        type="button"
        disabled={disabled}
        onClick={() => ref.current?.click()}
        onDragOver={(e) => {
          e.preventDefault()
          setOver(true)
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault()
          setOver(false)
          setError('')
          take(e.dataTransfer.files)
        }}
        className={cn(
          'flex w-full flex-col items-center gap-1.5 rounded-xl border-2 border-dashed p-5 text-center text-sm transition-colors',
          over ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40',
          disabled && 'pointer-events-none opacity-60',
        )}
      >
        <UploadCloud className="size-7 text-primary" aria-hidden />
        <span className="font-medium">{t('crm.expenses.dropTitle')}</span>
        <span className="text-xs text-muted-foreground">{t('crm.expenses.dropHint')}</span>
      </button>
      <input
        ref={ref}
        type="file"
        multiple
        hidden
        accept={ACCEPT.join(',')}
        onChange={(e) => {
          setError('')
          take(e.target.files)
          e.target.value = ''
        }}
      />
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
      {items.length ? (
        <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {items.map((it) => (
            <ReceiptRow key={it.key} item={it} disabled={disabled} onRemove={() => onChange(items.filter((x) => x.key !== it.key))} />
          ))}
        </ul>
      ) : null}
    </div>
  )
}

function ReceiptRow({ item, onRemove, disabled }: { item: PendingReceipt; onRemove: () => void; disabled?: boolean }) {
  const { t } = useTranslation()
  const url = useMemo(() => (item.file.type.startsWith('image/') ? URL.createObjectURL(item.file) : ''), [item.file])
  useEffect(() => () => {
    if (url) URL.revokeObjectURL(url)
  }, [url])
  return (
    <li className="flex items-center gap-2.5 rounded-lg border p-2">
      {url ? (
        <img src={url} alt="" className="size-11 shrink-0 rounded-md object-cover" />
      ) : (
        <span className="flex size-11 shrink-0 items-center justify-center rounded-md bg-destructive/10 text-destructive">
          <FileText className="size-5" aria-hidden />
        </span>
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{item.file.name}</p>
        <p className="text-xs text-muted-foreground">
          {fmtSize(item.file.size)}
          {item.error ? ` · ${item.error}` : ''}
        </p>
      </div>
      {item.state === 'uploading' ? <Loader2 className="size-4 animate-spin text-primary" aria-label={t('crm.expenses.uploading')} /> : null}
      {item.state === 'done' ? <CheckCircle2 className="size-4 text-success" aria-label={t('crm.expenses.uploaded')} /> : null}
      {item.state === 'error' ? <XCircle className="size-4 text-destructive" aria-label={t('crm.expenses.uploadFailed')} /> : null}
      {item.state === 'waiting' || item.state === 'error' ? (
        <Button type="button" size="icon" variant="ghost" className="size-7" disabled={disabled} aria-label={t('common.delete')} onClick={onRemove}>
          <X />
        </Button>
      ) : null}
    </li>
  )
}
